import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Filter, X } from "lucide-react";

const STATUSES = ["unclaimed", "claimed", "contacted"];

export default function FiltersPanel({ filters, setFilters, resetTo }) {
  const update = (k, v) => setFilters((p) => ({ ...p, [k]: v }));
  const [facets, setFacets] = useState({
    licensed_states: [], mailing_states: [], license_categories: [], sources: [],
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/contacts/facets");
        if (!cancelled) setFacets(data);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <aside
      className="bg-white border border-stone-300 p-5 sticky top-[80px]"
      data-testid="filters-panel"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-[#B85C38]" />
          <h3 className="font-display font-semibold tracking-tight">Filters</h3>
        </div>
        <Button
          variant="ghost" size="sm"
          onClick={() => setFilters(resetTo)}
          className="h-7 px-2 rounded-sm text-stone-500 hover:text-stone-900 text-xs"
          data-testid="reset-filters-btn"
        >
          <X className="h-3 w-3 mr-1" /> Clear
        </Button>
      </div>

      <div className="space-y-4">
        <div>
          <Label className="text-[10px] font-mono uppercase tracking-[0.2em] text-stone-600">
            Search
          </Label>
          <Input
            value={filters.search}
            onChange={(e) => update("search", e.target.value)}
            placeholder="Name, company, license, email…"
            className="mt-1.5 h-10 rounded-sm border-stone-300 focus-visible:ring-[#B85C38]"
            data-testid="filter-search-input"
          />
        </div>

        <div>
          <Label className="text-[10px] font-mono uppercase tracking-[0.2em] text-stone-600">
            Licensed State
          </Label>
          <Select value={filters.licensed_state} onValueChange={(v) => update("licensed_state", v)}>
            <SelectTrigger className="mt-1.5 h-10 rounded-sm border-stone-300" data-testid="filter-licensed-state">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">All licensed states</SelectItem>
              {facets.licensed_states.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-[10px] font-mono uppercase tracking-[0.2em] text-stone-600">
            Mailing State
          </Label>
          <Select value={filters.mailing_state} onValueChange={(v) => update("mailing_state", v)}>
            <SelectTrigger className="mt-1.5 h-10 rounded-sm border-stone-300" data-testid="filter-mailing-state">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">All mailing states</SelectItem>
              {facets.mailing_states.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {facets.license_categories.length > 0 && (
          <div>
            <Label className="text-[10px] font-mono uppercase tracking-[0.2em] text-stone-600">
              Category
            </Label>
            <Select value={filters.license_category} onValueChange={(v) => update("license_category", v)}>
              <SelectTrigger className="mt-1.5 h-10 rounded-sm border-stone-300" data-testid="filter-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {facets.license_categories.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div>
          <Label className="text-[10px] font-mono uppercase tracking-[0.2em] text-stone-600">
            Status
          </Label>
          <Select value={filters.status} onValueChange={(v) => update("status", v)}>
            <SelectTrigger className="mt-1.5 h-10 rounded-sm border-stone-300" data-testid="filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-[10px] font-mono uppercase tracking-[0.2em] text-stone-600">
            License expires within
          </Label>
          <Select value={filters.expires_window} onValueChange={(v) => update("expires_window", v)}>
            <SelectTrigger className="mt-1.5 h-10 rounded-sm border-stone-300" data-testid="filter-expires">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any time</SelectItem>
              <SelectItem value="30">30 days</SelectItem>
              <SelectItem value="60">60 days</SelectItem>
              <SelectItem value="90">90 days</SelectItem>
              <SelectItem value="180">6 months</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {facets.sources.length > 0 && (
          <div>
            <Label className="text-[10px] font-mono uppercase tracking-[0.2em] text-stone-600">
              Source
            </Label>
            <Select value={filters.source} onValueChange={(v) => update("source", v)}>
              <SelectTrigger className="mt-1.5 h-10 rounded-sm border-stone-300" data-testid="filter-source">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sources</SelectItem>
                {facets.sources.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </aside>
  );
}
